import React, { useEffect, useMemo, useState } from "react";
import { Alert, Checkbox, DatePicker, Form, Input, Modal, Radio, Select, Space, Typography, message } from "antd";
import AircraftSelector from "../../../../components/common/AircraftSelector";
import { UTCDateRangePicker } from "../../../../components/DateTimeInput";
import { useAircrafts } from "../../../../hooks/useReferenceData";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { formatScheduleDraftError } from "../lib/errorUtils";
import {
    AGENT_TRACK_TYPE,
    EDITING_TRACK_TYPE,
    HISTORY_TRACK_TYPE,
    LIVE_REFERENCE_TRACK_TYPE,
    REFERENCE_TRACK_TYPE,
    getTrackFlightCount,
    getTrackFlights,
    getWritableEditingTracks,
} from "../lib/trackUtils";
import { buildV1ScheduleOptions } from "../lib/v1ScheduleSourceOptions";
import { scheduleDraftDataService } from "../services/scheduleDraftDataService";
import { scheduleDraftReferenceDataService } from "../services/scheduleDraftReferenceDataService";
import { buildScheduleDraftSourceFlightsPayload } from "../services/scheduleDraftPayloadBuilders";

const { Paragraph, Text } = Typography;

dayjs.extend(utc);

export const SOURCE_TRACK_MODE_OPTIONS = [
    { label: "导入", value: "IMPORT" },
    { label: "挂载参考轨", value: "ATTACH" },
];

const SOURCE_TYPE_OPTIONS = [
    { label: "执行航班", value: "EXECUTION" },
    { label: "V2 历史内容", value: "TRACK" },
    { label: "旧版航班计划", value: "V1_SCHEDULE" },
    { label: "Excel 文件", value: "TABLE_EXCEL" },
    { label: "粘贴表格", value: "TABLE_PASTE" },
];

const TABLE_SOURCE_INPUT_MODE = {
    TABLE_EXCEL: "EXCEL_FILE",
    TABLE_PASTE: "PASTED_TABLE",
};

const NEW_TRACK_VALUE = "__NEW_TRACK__";

const V2_TRACK_SOURCE_TYPE_LABELS = {
    [HISTORY_TRACK_TYPE]: "历史快照",
    [AGENT_TRACK_TYPE]: "Agent 方案",
    [EDITING_TRACK_TYPE]: "已发布计划",
};

function formatTrackDateTime(value) {
    if (!value) {
        return "更新时间未知";
    }
    const date = dayjs.utc(value);
    return date.isValid() ? date.format("YYYY-MM-DD HH:mm") : "更新时间未知";
}

function getTrackType(track) {
    return String(track?.track_type || "").toUpperCase();
}

function isV2TrackSourceCandidate(track) {
    const trackType = getTrackType(track);
    if ([REFERENCE_TRACK_TYPE, LIVE_REFERENCE_TRACK_TYPE].includes(trackType)) {
        return false;
    }
    if ([HISTORY_TRACK_TYPE, AGENT_TRACK_TYPE].includes(trackType)) {
        return true;
    }
    return trackType === EDITING_TRACK_TYPE && (track?.is_editable === false || Boolean(track?.finalized_at));
}

function resolveSourceTrackExcludedId(targetTrackId) {
    if (targetTrackId && targetTrackId !== NEW_TRACK_VALUE) {
        return String(targetTrackId);
    }
    return "";
}

function buildV2TrackSourceLabel(track) {
    const trackType = getTrackType(track);
    const typeLabel = V2_TRACK_SOURCE_TYPE_LABELS[trackType] || "V2 轨";
    const flightCount = getTrackFlightCount(track);
    return `${track.name || "未命名轨"} · ${typeLabel} · ${flightCount || 0} 航班 · 更新 ${formatTrackDateTime(track.updated_at)}`;
}

export function buildV2TrackSourceOptions(draft, targetTrackId) {
    const excludedTrackId = resolveSourceTrackExcludedId(targetTrackId);
    return (draft?.tracks || [])
        .filter((track) => track?.id && String(track.id) !== excludedTrackId && isV2TrackSourceCandidate(track))
        .map((track) => ({
            label: buildV2TrackSourceLabel(track),
            value: String(track.id),
            title: track.summary || buildV2TrackSourceLabel(track),
        }));
}

export function shouldClearSelectedV2TrackSource(selectedTrackId, options = []) {
    return Boolean(selectedTrackId) && !options.some((option) => option.value === String(selectedTrackId));
}

export function resolveSourceTrackInitialMode(initialMode, { canImportSourceFlights = true, referenceOnly = false } = {}) {
    if (referenceOnly || !canImportSourceFlights) {
        return "ATTACH";
    }
    return initialMode === "ATTACH" ? "ATTACH" : "IMPORT";
}

function isSourceTypeSelectable(sourceType, { canImportSourceFlights, referenceOnly }) {
    return SOURCE_TYPE_OPTIONS.some((option) => option.value === sourceType)
        && (!TABLE_SOURCE_INPUT_MODE[sourceType] || (!referenceOnly && canImportSourceFlights));
}

function resolveInitialSourceType(sourceType, options) {
    return isSourceTypeSelectable(sourceType, options) ? sourceType : "EXECUTION";
}

function buildSourcePreviewMessage(preview) {
    const result = preview?.result;
    if (!result) {
        return "";
    }

    if (preview.status === "success") {
        return `已匹配 ${result.total || 0} 个来源航班。`;
    }

    if (preview.status === "tooMany") {
        return `已匹配 ${result.total || 0} 个来源航班，超过单次处理上限 ${result.limit || "系统"}。请缩小来源条件或来源内容。`;
    }

    return `未匹配到来源航班：${result.source_label || "当前来源"}。请检查来源内容。`;
}

function buildInitialPeriodRange(draft) {
    if (!draft?.period_start || !draft?.period_end) {
        return undefined;
    }

    return [dayjs.utc(draft.period_start), dayjs.utc(draft.period_end)];
}

function buildInitialDateShiftTarget(draft) {
    return draft?.period_start ? dayjs.utc(draft.period_start) : undefined;
}

export function buildSourceTypeChangePatch(sourceType, draft) {
    if (TABLE_SOURCE_INPUT_MODE[sourceType]) {
        return {
            mode: "IMPORT",
            sourceScheduleId: undefined,
            sourceTrackId: undefined,
            periodRange: undefined,
            attachReference: false,
        };
    }

    if (sourceType === "V1_SCHEDULE") {
        return {
            sourceTrackId: undefined,
            periodRange: undefined,
        };
    }

    if (sourceType === "TRACK") {
        return {
            sourceScheduleId: undefined,
            sourceTrackId: undefined,
            periodRange: undefined,
        };
    }

    if (sourceType === "EXECUTION") {
        return {
            sourceScheduleId: undefined,
            sourceTrackId: undefined,
            periodRange: buildInitialPeriodRange(draft),
        };
    }

    return {};
}

export function getTableImportInputMode(sourceType) {
    return TABLE_SOURCE_INPUT_MODE[sourceType] || null;
}

export function buildEditingTrackOptions(draft) {
    const editingTracks = getWritableEditingTracks(draft);
    return [
        ...editingTracks.map((track) => ({
            label: track.name || "未命名编辑轨",
            value: track.id,
        })),
        { label: "+ 新建编辑轨", value: NEW_TRACK_VALUE },
    ];
}

function resolveDefaultTargetTrackId(draft, currentEditingTrackId) {
    const editingTracks = getWritableEditingTracks(draft);
    const currentTrack = editingTracks.find((track) => String(track.id) === String(currentEditingTrackId));
    return currentTrack?.id || editingTracks[0]?.id || undefined;
}

function findTrackById(draft, trackId) {
    if (trackId === undefined || trackId === null || trackId === "") {
        return null;
    }
    return (draft?.tracks || []).find((track) => String(track.id) === String(trackId)) || null;
}

function normalizeAircraftReg(value) {
    return String(value || "").trim().toUpperCase();
}

function firstSingleRegistration(values = []) {
    const registrations = [...new Set(values.map(normalizeAircraftReg).filter(Boolean))];
    return registrations.length === 1 ? registrations[0] : "";
}

export function resolveTrackAircraftReg(track) {
    if (!track) {
        return "";
    }

    const explicitAircraft = firstSingleRegistration([
        track.aircraft_reg,
        track.aircraftReg,
        track.aircraft?.registration,
        track.aircraft?.aircraft_reg,
        track.metrics?.aircraft_reg,
        track.metrics?.aircraftReg,
        track.agent_context?.aircraft_reg,
    ]);
    if (explicitAircraft) {
        return explicitAircraft;
    }

    const metricScope = track.metrics?.aircraft_scope || track.metrics?.aircraftScope;
    const metricAircraft = firstSingleRegistration(Array.isArray(metricScope) ? metricScope : []);
    if (metricAircraft) {
        return metricAircraft;
    }

    return firstSingleRegistration(getTrackFlights(track).map((flight) => flight?.aircraft_reg));
}

function hasLockedTargetTrack(lockedTargetTrackId) {
    return lockedTargetTrackId !== undefined && lockedTargetTrackId !== null && lockedTargetTrackId !== "";
}

function resolveImportedTrackId(flights, importPayload, values) {
    const firstFlight = Array.isArray(flights) ? flights[0] : null;
    if (firstFlight?.track_id) {
        return firstFlight.track_id;
    }
    if (importPayload?.target_track_id) {
        return importPayload.target_track_id;
    }
    if (values?.targetTrackId && values.targetTrackId !== NEW_TRACK_VALUE) {
        return values.targetTrackId;
    }
    return undefined;
}

function buildAttachReferencePayload(values, pairedTrackId) {
    const payload = {
        sourceType: values.sourceType,
        sourceScheduleId: values.sourceScheduleId,
        sourceTrackId: values.sourceTrackId,
        sourceId: values.sourceId,
        sourceAircraftReg: values.sourceAircraftReg,
        periodRange: values.periodRange,
        dateShiftTarget: values.dateShiftTarget,
    };

    if (pairedTrackId) {
        payload.paired_with_track_id = pairedTrackId;
    }

    return payload;
}

const ScheduleDraftSourceTrackModal = ({
    open,
    draft,
    currentEditingTrackId,
    onCancel,
    onAttachReferenceTrack,
    onImportSourceFlights,
    onOpenTableImport,
    attachingReferenceTrack,
    importingSourceFlights,
    canImportSourceFlights = true,
    dirtyBlockReason = "",
    initialValues = null,
    referenceOnly = false,
    lockedTargetTrackId = null,
}) => {
    const [form] = Form.useForm();
    const { aircrafts, isLoading: aircraftsLoading } = useAircrafts({ enabled: open });
    const [sourcePreview, setSourcePreview] = useState(null);
    const [sourcePreviewLoading, setSourcePreviewLoading] = useState(false);
    const [v1Schedules, setV1Schedules] = useState([]);
    const [v1SchedulesLoading, setV1SchedulesLoading] = useState(false);
    const mode = Form.useWatch("mode", form);
    const sourceType = Form.useWatch("sourceType", form);
    const targetTrackId = Form.useWatch("targetTrackId", form);
    const attachReference = Form.useWatch("attachReference", form);
    const tableInputMode = getTableImportInputMode(sourceType);
    const isTableSource = Boolean(tableInputMode);
    const targetTrackLocked = hasLockedTargetTrack(lockedTargetTrackId);
    const effectiveMode = targetTrackLocked || isTableSource ? "IMPORT" : mode;
    const confirmLoading = sourcePreviewLoading || (effectiveMode === "IMPORT" ? importingSourceFlights || (attachReference ? attachingReferenceTrack : false) : attachingReferenceTrack);
    const modeOptions = SOURCE_TRACK_MODE_OPTIONS.map((option) => (option.value === "IMPORT" ? { ...option, disabled: !canImportSourceFlights } : option));
    const sourceTypeOptions = useMemo(
        () => SOURCE_TYPE_OPTIONS.filter((option) => isSourceTypeSelectable(option.value, { canImportSourceFlights, referenceOnly })),
        [canImportSourceFlights, referenceOnly],
    );
    const editingTrackOptions = useMemo(() => buildEditingTrackOptions(draft), [draft]);
    const importTargetOptions = useMemo(
        () => (isTableSource ? editingTrackOptions.filter((option) => option.value !== NEW_TRACK_VALUE) : editingTrackOptions),
        [editingTrackOptions, isTableSource],
    );
    const lockedTargetTrack = useMemo(() => findTrackById(draft, lockedTargetTrackId), [draft, lockedTargetTrackId]);
    const lockedTargetTrackLabel = lockedTargetTrack?.name || "当前编辑轨";
    const v1ScheduleOptions = useMemo(() => buildV1ScheduleOptions(v1Schedules), [v1Schedules]);
    const v2TrackSourceOptions = useMemo(
        () => buildV2TrackSourceOptions(draft, effectiveMode === "IMPORT" ? targetTrackId : undefined),
        [draft, effectiveMode, targetTrackId],
    );

    useEffect(() => {
        if (!open) {
            return;
        }

        const initialSourceType = resolveInitialSourceType(initialValues?.sourceType || "EXECUTION", { canImportSourceFlights, referenceOnly });
        const initialDateShiftTarget = initialValues?.dateShiftTarget ? dayjs.utc(initialValues.dateShiftTarget) : buildInitialDateShiftTarget(draft);
        const isExecutionSource = initialSourceType === "EXECUTION";
        const isV1Source = initialSourceType === "V1_SCHEDULE";
        const isTrackSource = initialSourceType === "TRACK";

        const initialTargetTrackId = targetTrackLocked ? lockedTargetTrackId : initialValues?.targetTrackId || resolveDefaultTargetTrackId(draft, currentEditingTrackId);

        form.setFieldsValue({
            mode: targetTrackLocked ? "IMPORT" : resolveSourceTrackInitialMode(initialValues?.mode, { canImportSourceFlights, referenceOnly }),
            sourceType: initialSourceType,
            sourceScheduleId: isV1Source ? initialValues?.sourceScheduleId : undefined,
            sourceTrackId: isTrackSource ? initialValues?.sourceTrackId || initialValues?.sourceId || initialValues?.source_id : undefined,
            sourceAircraftReg: initialValues?.sourceAircraftReg || initialValues?.source_aircraft_reg || undefined,
            periodRange: isExecutionSource ? buildInitialPeriodRange(draft) : undefined,
            aircraftRegs: undefined,
            statuses: undefined,
            includeCancelled: undefined,
            dateShiftTarget: initialDateShiftTarget?.isValid?.() === false ? buildInitialDateShiftTarget(draft) : initialDateShiftTarget,
            targetTrackId: initialTargetTrackId,
            newTrackName: targetTrackLocked ? undefined : initialValues?.newTrackName,
            overrideAircraftReg: initialValues?.overrideAircraftReg || initialValues?.override_aircraft_reg || resolveTrackAircraftReg(findTrackById(draft, initialTargetTrackId)),
            attachReference: Boolean(initialValues?.attachReference),
        });
        setSourcePreview(null);
    }, [canImportSourceFlights, currentEditingTrackId, draft, form, initialValues, lockedTargetTrackId, open, referenceOnly, targetTrackLocked]);

    useEffect(() => {
        if (!open || sourceType !== "V1_SCHEDULE") {
            return undefined;
        }

        let cancelled = false;
        setV1SchedulesLoading(true);
        scheduleDraftReferenceDataService.getV1Schedules()
            .then((items) => {
                if (!cancelled) {
                    setV1Schedules(items || []);
                }
            })
            .catch((error) => {
                if (!cancelled) {
                    const errorMessage = formatScheduleDraftError(error, "加载 V1 计划列表失败");
                    setSourcePreview({ status: "error", error: errorMessage });
                    message.error(errorMessage);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setV1SchedulesLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [open, sourceType]);

    useEffect(() => {
        if (!open || sourceType !== "TRACK") {
            return;
        }
        const selectedTrackId = form.getFieldValue("sourceTrackId");
        if (shouldClearSelectedV2TrackSource(selectedTrackId, v2TrackSourceOptions)) {
            form.setFieldsValue({ sourceTrackId: undefined });
            setSourcePreview(null);
        }
    }, [form, open, sourceType, v2TrackSourceOptions]);

    const ensureSourceHasFlights = async (values) => {
        if (!draft?.id) {
            message.error("缺少航班计划 ID，无法预览来源航班");
            return false;
        }

        setSourcePreviewLoading(true);
        try {
            const result = await scheduleDraftDataService.previewSourceFlights(draft.id, buildScheduleDraftSourceFlightsPayload(values));
            const nextPreview = {
                status: result?.has_more ? "tooMany" : result?.total ? "success" : "empty",
                result,
            };
            setSourcePreview(nextPreview);
            if (result?.has_more) {
                message.warning(buildSourcePreviewMessage(nextPreview));
                return false;
            }
            if (!result?.total) {
                message.warning(buildSourcePreviewMessage(nextPreview));
                return false;
            }
            return true;
        } catch (error) {
            const errorMessage = formatScheduleDraftError(error, "预览来源航班失败");
            setSourcePreview({ status: "error", error: errorMessage });
            message.error(errorMessage);
            return false;
        } finally {
            setSourcePreviewLoading(false);
        }
    };

    const handleFormValuesChange = (changedValues) => {
        if (Object.prototype.hasOwnProperty.call(changedValues, "sourceType")) {
            const patch = buildSourceTypeChangePatch(changedValues.sourceType, draft);
            if (changedValues.sourceType !== "EXECUTION") {
                patch.sourceAircraftReg = undefined;
            }
            if (TABLE_SOURCE_INPUT_MODE[changedValues.sourceType] && form.getFieldValue("targetTrackId") === NEW_TRACK_VALUE) {
                const targetTrackIdForTable = resolveDefaultTargetTrackId(draft, currentEditingTrackId);
                patch.targetTrackId = targetTrackIdForTable;
                patch.overrideAircraftReg = resolveTrackAircraftReg(findTrackById(draft, targetTrackIdForTable));
            }
            form.setFieldsValue(patch);
        }
        if (Object.prototype.hasOwnProperty.call(changedValues, "targetTrackId")) {
            form.setFieldsValue({
                overrideAircraftReg: resolveTrackAircraftReg(findTrackById(draft, changedValues.targetTrackId)),
            });
        }
        setSourcePreview(null);
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            const submittedValues = targetTrackLocked
                ? { ...values, mode: "IMPORT", targetTrackId: lockedTargetTrackId, newTrackName: undefined }
                : values;
            if (dirtyBlockReason) {
                message.warning(dirtyBlockReason);
                return;
            }

            const nextTableInputMode = getTableImportInputMode(submittedValues.sourceType);
            if (referenceOnly && nextTableInputMode) {
                return;
            }
            if (nextTableInputMode) {
                onOpenTableImport?.(nextTableInputMode, submittedValues);
                return;
            }

            const hasSourceFlights = await ensureSourceHasFlights(submittedValues);
            if (!hasSourceFlights) {
                return;
            }

            const importPayload = {
                ...submittedValues,
                target_track_id: submittedValues.targetTrackId === NEW_TRACK_VALUE ? undefined : submittedValues.targetTrackId,
            };

            if (submittedValues.mode === "IMPORT") {
                if (!canImportSourceFlights) {
                    return;
                }
                const importedFlights = await onImportSourceFlights?.(importPayload);
                if (submittedValues.attachReference) {
                    const pairedTrackId = resolveImportedTrackId(importedFlights, importPayload, submittedValues);
                    await onAttachReferenceTrack?.(buildAttachReferencePayload(submittedValues, pairedTrackId));
                }
                return;
            }
            await onAttachReferenceTrack?.(buildAttachReferencePayload(submittedValues));
            onCancel?.();
        } catch (error) {
            if (error?.errorFields) {
                return;
            }
        }
    };

    const showImportTarget = !referenceOnly && effectiveMode === "IMPORT";
    const showNewTrackName = !isTableSource && !targetTrackLocked && targetTrackId === NEW_TRACK_VALUE;
    const okText = isTableSource ? "继续到表格导入" : effectiveMode === "IMPORT" ? (attachReference ? "导入并挂载参考轨" : "导入为新计划内容") : "挂载参考轨";
    const modalTitle = referenceOnly ? "挂载参考轨" : initialValues?.sourceType === "V1_SCHEDULE" && initialValues?.mode === "IMPORT" ? "导入旧版航班计划" : "添加来源";

    return (
        <Modal
            title={modalTitle}
            open={open}
            onCancel={onCancel}
            onOk={handleSubmit}
            okText={okText}
            cancelText="取消"
            confirmLoading={confirmLoading}
            destroyOnClose
        >
            <Space direction="vertical" size={12} style={{ width: "100%" }}>
                {sourcePreview?.status === "error" ? (
                    <Alert type="error" showIcon message={sourcePreview.error} />
                ) : sourcePreview ? (
                    <Alert type={sourcePreview.status === "success" ? "success" : "warning"} showIcon message={buildSourcePreviewMessage(sourcePreview)} />
                ) : null}

                <Form form={form} layout="vertical" onValuesChange={handleFormValuesChange}>
                    <Form.Item name="sourceType" label="来源类型" rules={[{ required: true, message: "请选择来源类型" }]}>
                        <Radio.Group options={sourceTypeOptions} optionType="button" />
                    </Form.Item>

                    {sourceType === "EXECUTION" ? (
                        <>
                            <Form.Item name="sourceAircraftReg" label="来源飞机" rules={[{ required: true, message: "请选择来源飞机" }]}>
                                <AircraftSelector
                                    allowClear
                                    valueType="registration"
                                    displayMode="detailed"
                                    aircrafts={aircrafts}
                                    loading={aircraftsLoading}
                                    placeholder="选择来源飞机"
                                    style={{ width: "100%" }}
                                />
                            </Form.Item>
                            <Form.Item name="periodRange" label="执行航班时间范围">
                                <UTCDateRangePicker style={{ width: "100%" }} format="YYYY-MM-DD" placeholder={["开始日期", "结束日期"]} />
                            </Form.Item>
                        </>
                    ) : null}

                    {sourceType === "TRACK" ? (
                        <Form.Item name="sourceTrackId" label="V2 历史内容" rules={[{ required: true, message: "请选择 V2 历史内容来源" }]}>
                            <Select
                                showSearch
                                placeholder="选择当前计划内的历史快照、Agent 方案或已发布轨"
                                optionFilterProp="label"
                                options={v2TrackSourceOptions}
                                notFoundContent="当前计划暂无可作为来源的历史内容"
                            />
                        </Form.Item>
                    ) : null}

                    {sourceType === "V1_SCHEDULE" ? (
                        <Form.Item name="sourceScheduleId" label="旧版航班计划" rules={[{ required: true, message: "请选择旧版航班计划" }]}>
                            <Select
                                showSearch
                                loading={v1SchedulesLoading}
                                placeholder="按计划名、标签、飞机、日期范围、状态、航班数或更新时间搜索"
                                optionFilterProp="searchText"
                                options={v1ScheduleOptions}
                            />
                        </Form.Item>
                    ) : null}

                    {!isTableSource && !referenceOnly && !targetTrackLocked ? (
                        <Form.Item name="mode" label="新增模式" rules={[{ required: true, message: "请选择新增模式" }]}>
                            <Radio.Group options={modeOptions} optionType="button" buttonStyle="solid" />
                        </Form.Item>
                    ) : null}

                    {showImportTarget ? (
                        <>
                            {targetTrackLocked ? (
                                <>
                                    <Form.Item name="targetTrackId" hidden rules={[{ required: true, message: "缺少锁定的导入目标编辑轨" }]}>
                                        <Input />
                                    </Form.Item>
                                    <div className="schedule-draft-source-track-note">
                                        <Text strong>导入目标编辑轨</Text>
                                        <Paragraph type="secondary">{lockedTargetTrackLabel}</Paragraph>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <Form.Item name="targetTrackId" label="导入目标编辑轨" rules={[{ required: true, message: "请选择导入目标编辑轨" }]}>
                                        <Select
                                            showSearch
                                            placeholder={isTableSource ? "选择编辑轨" : "选择编辑轨或新建"}
                                            optionFilterProp="label"
                                            options={importTargetOptions}
                                        />
                                    </Form.Item>

                                    {showNewTrackName ? (
                                        <Form.Item name="newTrackName" label="新编辑轨名称" rules={[{ required: true, message: "请输入新编辑轨名称" }]}>
                                            <Input placeholder="例如 五月第一版" />
                                        </Form.Item>
                                    ) : null}
                                </>
                            )}

                            <Form.Item name="overrideAircraftReg" label="导入飞机" rules={[{ required: true, message: "请选择导入飞机" }]}>
                                <AircraftSelector
                                    allowClear
                                    valueType="registration"
                                    displayMode="detailed"
                                    aircrafts={aircrafts}
                                    loading={aircraftsLoading}
                                    placeholder="选择导入飞机"
                                    style={{ width: "100%" }}
                                />
                            </Form.Item>

                            {!isTableSource ? (
                                <Form.Item name="attachReference" valuePropName="checked">
                                    <Checkbox>同时创建只读参考轨</Checkbox>
                                </Form.Item>
                            ) : null}
                        </>
                    ) : null}

                    {!isTableSource ? (
                        <Form.Item name="dateShiftTarget" label="日期平移到">
                            <DatePicker allowClear style={{ width: "100%" }} />
                        </Form.Item>
                    ) : null}
                </Form>
            </Space>
        </Modal>
    );
};

export default ScheduleDraftSourceTrackModal;
